import { StudyWorkflowStepApproval } from 'interfaces/study';
import { formatDateTimeForUser } from 'utils/dateTime';

import { ImageAnalysisTaskStatus } from './studyWorkflowJobStatus';
import { getLatestWorkflowStepApproval, getWorkflowApproverName } from './studyWorkflowModel';

export const USER_ACCEPTANCE_WORKFLOW_STEP_IDS = [
  'dataset-preparation',
  'case-image-analysis',
  'marker-normalization',
  'rna-probes',
] as const;

export type UserAcceptanceWorkflowStepId = (typeof USER_ACCEPTANCE_WORKFLOW_STEP_IDS)[number];

export interface WorkflowModalityCount {
  label: string;
  count: number;
}

export interface WorkflowTaskCountSnapshot {
  id: string;
  modalityLabel: string;
  label: string;
  totalCount: number;
  completedCount: number;
  runningCount: number;
  pendingCount: number;
  failedCount: number;
  cancelledCount: number;
  notStartedCount: number;
}

export interface WorkflowStepCompletion {
  mode: 'user-acceptance' | 'evidence' | 'none';
  isDone: boolean;
  isStale: boolean;
  canMarkDone: boolean;
  showReopen: boolean;
  blockedReason?: string;
  summary: string;
  digest?: string;
  acceptedSummary?: string;
  acceptedAt?: string;
  acceptedByName?: string;
}

export interface WorkflowStepCompletionInput {
  approvals?: StudyWorkflowStepApproval[];
  approverNames?: Record<string, string> | null;
  canEditStudy: boolean;
  datasetPreparation: {
    isReady: boolean;
    isLoading?: boolean;
    uploadedSlideCount: number;
    activeIngestCount: number;
    ingestedSlideCount: number;
    slidesWithStainingMetadataCount: number;
    slideRecordCount: number;
    modalityCounts: WorkflowModalityCount[];
  };
  caseOrganization: {
    isReady: boolean;
    slideRecordCount: number;
    ungroupedSlideCount: number;
    missingStainingModalityCount: number;
    caseSlideCount: number;
    caseCount: number;
  };
  caseImageAnalysis: {
    isReady: boolean;
    isLoading?: boolean;
    blockedReason?: string;
    noSlidesWithModality: boolean;
    tasks: ImageAnalysisTaskStatus[];
  };
  markerAssignments: {
    isReady: boolean;
    isApproved: boolean;
    hasSavedPanels: boolean;
    approvedSlidesCount: number;
    totalSlidesCount: number;
  };
  markerNormalization: {
    isReady: boolean;
    isLoading?: boolean;
    multiplexSlideCount: number;
  };
  rnaProbes: {
    isApplicable: boolean;
    isLoading?: boolean;
    isReady: boolean;
    blockedReason?: string;
    noMultiplexSlides: boolean;
    task: ImageAnalysisTaskStatus | null;
  };
}

const STUDY_EDIT_BLOCKED_REASON = 'Study editing permission is required to mark this step done.';

const sortValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(sortValue);
  }
  if (!value || typeof value !== 'object') {
    return value;
  }
  return Object.keys(value as Record<string, unknown>)
    .sort()
    .reduce<Record<string, unknown>>((sorted, key) => {
      sorted[key] = sortValue((value as Record<string, unknown>)[key]);
      return sorted;
    }, {});
};

export const workflowStepStateDigest = (value: unknown): string => JSON.stringify(sortValue(value));

const countPhrase = (count: number, singular: string, plural = `${singular}s`) =>
  `${count} ${count === 1 ? singular : plural}`;

const taskIsSuccessful = (task: WorkflowTaskCountSnapshot) =>
  task.totalCount > 0 &&
  task.completedCount === task.totalCount &&
  task.runningCount === 0 &&
  task.pendingCount === 0 &&
  task.failedCount === 0 &&
  task.cancelledCount === 0 &&
  task.notStartedCount === 0;

const visibleTaskCounts = (tasks: ImageAnalysisTaskStatus[]): WorkflowTaskCountSnapshot[] =>
  tasks
    .filter((task) => task.totalCount > 0)
    .map((task) => ({
      id: task.id,
      modalityLabel: task.modalityLabel,
      label: task.label,
      totalCount: task.totalCount,
      completedCount: task.completedCount,
      runningCount: task.runningCount,
      pendingCount: task.pendingCount,
      failedCount: task.failedCount,
      cancelledCount: task.cancelledCount,
      notStartedCount: task.notStartedCount,
    }));

const taskCountClause = (task: WorkflowTaskCountSnapshot, { includeModality }: { includeModality: boolean }) => {
  const extras = [
    task.runningCount ? `${task.runningCount} running` : null,
    task.pendingCount ? `${task.pendingCount} pending` : null,
    task.failedCount ? `${task.failedCount} failed` : null,
    task.cancelledCount ? `${task.cancelledCount} cancelled` : null,
    task.notStartedCount ? `${task.notStartedCount} not started` : null,
  ].filter((part): part is string => Boolean(part));
  const subject = includeModality ? `${task.modalityLabel} ${task.label}` : task.label;
  const completed = `${task.completedCount} of ${task.totalCount} completed`;
  const detail = extras.length ? `${completed}, ${extras.join(', ')}` : completed;
  return includeModality ? `${subject} ${detail}` : `${subject}: ${detail}`;
};

export const datasetPreparationSummary = (input: WorkflowStepCompletionInput['datasetPreparation']) =>
  `${input.ingestedSlideCount} ingested, ${input.activeIngestCount} in ingest, ${input.uploadedSlideCount} uploaded; ${input.slidesWithStainingMetadataCount}/${input.slideRecordCount} with staining modality`;

export const caseOrganizationSummary = (input: WorkflowStepCompletionInput['caseOrganization']) =>
  `${countPhrase(input.caseSlideCount, 'slide')} in ${countPhrase(
    input.caseCount,
    'case'
  )}; all have a staining modality`;

export const caseImageAnalysisSummary = (input: WorkflowStepCompletionInput['caseImageAnalysis']) => {
  if (input.noSlidesWithModality) {
    return 'No slides with a staining modality in this study.';
  }
  const tasks = visibleTaskCounts(input.tasks);
  return tasks.length
    ? tasks.map((task) => taskCountClause(task, { includeModality: true })).join('; ')
    : 'No analysis jobs are shown.';
};

export const markerNormalizationSummary = (multiplexSlideCount: number) =>
  countPhrase(multiplexSlideCount, 'multiplex slide', 'multiplex slides');

export const rnaProbeSummary = (input: WorkflowStepCompletionInput['rnaProbes']) => {
  const visibleTask = input.task ? visibleTaskCounts([input.task])[0] : undefined;
  if (input.noMultiplexSlides || !visibleTask) {
    return 'No multiplex slides in this study.';
  }
  return taskCountClause(visibleTask, { includeModality: false });
};

export const markerAssignmentsSummary = ({
  input,
  approval,
  approverNames,
}: {
  input: WorkflowStepCompletionInput['markerAssignments'];
  approval?: StudyWorkflowStepApproval;
  approverNames?: Record<string, string> | null;
}) => {
  if (input.hasSavedPanels) {
    return `${input.approvedSlidesCount} of ${input.totalSlidesCount} multiplex slides approved through panels`;
  }
  if (!approval) {
    return 'Marker assignments are not approved.';
  }
  return `Approved on ${formatDateTimeForUser(approval.approvedAt)} by ${getWorkflowApproverName(
    approval,
    approverNames
  )}`;
};

const noneCompletion = (): WorkflowStepCompletion => ({
  mode: 'none',
  isDone: false,
  isStale: false,
  canMarkDone: false,
  showReopen: false,
  summary: '',
});

const userAcceptanceCompletion = ({
  approvals,
  approverNames,
  canEditStudy,
  stepId,
  isReady,
  isLoading = false,
  blockedReason,
  summary,
  digest,
}: {
  approvals?: StudyWorkflowStepApproval[];
  approverNames?: Record<string, string> | null;
  canEditStudy: boolean;
  stepId: string;
  isReady: boolean;
  isLoading?: boolean;
  blockedReason?: string;
  summary: string;
  digest: string;
}): WorkflowStepCompletion => {
  const latest = getLatestWorkflowStepApproval(approvals, stepId);
  const hasDigestAcceptance = latest?.action === 'approved' && Boolean(latest.stateDigest);
  const digestMatches = Boolean(hasDigestAcceptance && latest?.stateDigest === digest);
  // Empty job rows while status is loading are not a new snapshot. Keep the accepted state
  // until the real counts arrive, instead of showing a false mismatch.
  const holdAcceptedState = isLoading && hasDigestAcceptance;
  const isDone = holdAcceptedState || (isReady && digestMatches);
  const isStale = isReady && Boolean(hasDigestAcceptance && !digestMatches);
  const permissionReason = canEditStudy ? undefined : STUDY_EDIT_BLOCKED_REASON;

  return {
    mode: 'user-acceptance',
    isDone,
    isStale,
    canMarkDone: isReady && !blockedReason && canEditStudy && !isDone,
    showReopen: Boolean(hasDigestAcceptance && canEditStudy && !holdAcceptedState),
    blockedReason: isReady ? blockedReason || permissionReason : undefined,
    summary: holdAcceptedState && latest?.stateSummary ? latest.stateSummary : summary,
    digest: holdAcceptedState && latest?.stateDigest ? latest.stateDigest : digest,
    acceptedSummary: hasDigestAcceptance ? latest?.stateSummary : undefined,
    acceptedAt: hasDigestAcceptance ? latest?.approvedAt : undefined,
    acceptedByName: latest && hasDigestAcceptance ? getWorkflowApproverName(latest, approverNames) : undefined,
  };
};

export const buildWorkflowStepCompletions = (
  input: WorkflowStepCompletionInput
): Record<string, WorkflowStepCompletion> => {
  const datasetSnapshot = {
    uploadedSlideCount: input.datasetPreparation.uploadedSlideCount,
    activeIngestCount: input.datasetPreparation.activeIngestCount,
    ingestedSlideCount: input.datasetPreparation.ingestedSlideCount,
    slidesWithStainingMetadataCount: input.datasetPreparation.slidesWithStainingMetadataCount,
    slideRecordCount: input.datasetPreparation.slideRecordCount,
    modalityCounts: [...input.datasetPreparation.modalityCounts].sort((left, right) =>
      left.label.localeCompare(right.label)
    ),
  };
  const caseOrganizationDone =
    input.caseOrganization.isReady &&
    input.caseOrganization.slideRecordCount > 0 &&
    input.caseOrganization.ungroupedSlideCount === 0 &&
    input.caseOrganization.missingStainingModalityCount === 0;
  const imageAnalysisSnapshot = input.caseImageAnalysis.noSlidesWithModality
    ? { noSlidesWithModality: true }
    : { tasks: visibleTaskCounts(input.caseImageAnalysis.tasks) };
  const visibleRnaTask = input.rnaProbes.task ? visibleTaskCounts([input.rnaProbes.task])[0] : undefined;
  const rnaSnapshot =
    input.rnaProbes.noMultiplexSlides || !visibleRnaTask ? { noMultiplexSlides: true } : { task: visibleRnaTask };
  const markerApproval = getLatestWorkflowStepApproval(input.approvals, 'marker-assignments');
  const datasetPreparation = userAcceptanceCompletion({
    approvals: input.approvals,
    approverNames: input.approverNames,
    canEditStudy: input.canEditStudy,
    stepId: 'dataset-preparation',
    isReady: input.datasetPreparation.isReady,
    isLoading: input.datasetPreparation.isLoading,
    summary: datasetPreparationSummary(input.datasetPreparation),
    digest: workflowStepStateDigest(datasetSnapshot),
  });
  const caseOrganization: WorkflowStepCompletion = {
    mode: 'evidence',
    isDone: caseOrganizationDone,
    isStale: false,
    canMarkDone: false,
    showReopen: false,
    summary: caseOrganizationDone ? caseOrganizationSummary(input.caseOrganization) : '',
  };
  const manualImageAnalysis = userAcceptanceCompletion({
    approvals: input.approvals,
    approverNames: input.approverNames,
    canEditStudy: input.canEditStudy,
    stepId: 'case-image-analysis',
    isReady: input.caseImageAnalysis.isReady,
    isLoading: input.caseImageAnalysis.isLoading,
    blockedReason: input.caseImageAnalysis.blockedReason,
    summary: caseImageAnalysisSummary(input.caseImageAnalysis),
    digest: workflowStepStateDigest(imageAnalysisSnapshot),
  });
  const visibleImageAnalysisTasks = visibleTaskCounts(input.caseImageAnalysis.tasks);
  const imageAnalysisAutoDone =
    datasetPreparation.isDone &&
    caseOrganization.isDone &&
    input.caseImageAnalysis.isReady &&
    !input.caseImageAnalysis.blockedReason &&
    !input.caseImageAnalysis.noSlidesWithModality &&
    visibleImageAnalysisTasks.length > 0 &&
    visibleImageAnalysisTasks.every(taskIsSuccessful);

  return {
    'dataset-preparation': datasetPreparation,
    'case-organization': caseOrganization,
    'case-image-analysis': imageAnalysisAutoDone
      ? {
          ...manualImageAnalysis,
          mode: 'evidence',
          isDone: true,
          isStale: false,
          canMarkDone: false,
          showReopen: false,
          blockedReason: undefined,
          acceptedSummary: undefined,
          acceptedAt: undefined,
          acceptedByName: undefined,
        }
      : manualImageAnalysis,
    'marker-assignments': {
      mode: 'evidence',
      isDone: input.markerAssignments.isReady && input.markerAssignments.isApproved,
      isStale: false,
      canMarkDone: false,
      showReopen: false,
      summary:
        input.markerAssignments.isReady && input.markerAssignments.isApproved
          ? markerAssignmentsSummary({
              input: input.markerAssignments,
              approval: markerApproval,
              approverNames: input.approverNames,
            })
          : '',
    },
    'marker-normalization': userAcceptanceCompletion({
      approvals: input.approvals,
      approverNames: input.approverNames,
      canEditStudy: input.canEditStudy,
      stepId: 'marker-normalization',
      isReady: input.markerNormalization.isReady,
      isLoading: input.markerNormalization.isLoading,
      summary: markerNormalizationSummary(input.markerNormalization.multiplexSlideCount),
      digest: workflowStepStateDigest({ multiplexSlideCount: input.markerNormalization.multiplexSlideCount }),
    }),
    'rna-probes': input.rnaProbes.isApplicable
      ? userAcceptanceCompletion({
          approvals: input.approvals,
          approverNames: input.approverNames,
          canEditStudy: input.canEditStudy,
          stepId: 'rna-probes',
          isLoading: input.rnaProbes.isLoading,
          isReady: input.rnaProbes.isReady,
          blockedReason: input.rnaProbes.blockedReason,
          summary: rnaProbeSummary(input.rnaProbes),
          digest: workflowStepStateDigest(rnaSnapshot),
        })
      : noneCompletion(),
  };
};
